import {useQuery} from 'react-query'
import {useRef, useState, useCallback, useMemo} from 'react'
import {QRCodeSVG} from 'qrcode.react'
import {toJpeg, toPng, toBlob} from 'html-to-image'
import CopyToClipboard from 'react-copy-to-clipboard'
import {toast} from 'react-toastify'
import axios from 'axios'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {toAbsoluteUrl, apiURL} from '_metronic/helpers'
import {
  DayFilter,
  ReferredUser,
  getReferralCode,
  getReferralSummary,
  getReferredList,
} from '_metronic/layout/core/requests'
import {Pagination} from '../../../_metronic/layout/components/Pagination'
import {SortOpts, setupList} from '../setup/core/_requests'
import {useNavigate} from 'react-router-dom'
import {Loading} from '../apps/user-management/users-list/components/loading/Loading'
import {useAuth} from '../auth'
import {PerformancePoster, PosterFrame, TradePoster} from './shareCards'
import {BookRow, buildShareTrades, formatPrice, formatR, ShareTrade, SetupLike} from './shareMath'

// ─── Persist social handles ───────────────────────────────────────────────────

const SOCIAL_STORAGE_KEY = 'social_handles_v2'

type SocialPlatform = 'x' | 'telegram' | 'instagram' | 'youtube' | 'tiktok' | 'discord' | 'website'
type SharePlatform = 'x' | 'telegram' | 'whatsapp' | 'linkedin' | 'facebook' | 'reddit'
type SocialHandles = Record<SocialPlatform, string>

const EMPTY_HANDLES: SocialHandles = {
  x: '',
  telegram: '',
  instagram: '',
  youtube: '',
  tiktok: '',
  discord: '',
  website: '',
}

function loadHandles(): SocialHandles {
  try {
    const savedV2 = localStorage.getItem(SOCIAL_STORAGE_KEY)
    if (savedV2) return {...EMPTY_HANDLES, ...JSON.parse(savedV2)}
    const savedLegacy = localStorage.getItem('social_handles')
    if (savedLegacy) return {...EMPTY_HANDLES, ...JSON.parse(savedLegacy)}
  } catch {}
  return EMPTY_HANDLES
}

function saveHandles(handles: SocialHandles) {
  try {
    localStorage.setItem(SOCIAL_STORAGE_KEY, JSON.stringify(handles))
  } catch {}
}

function cleanHandle(platform: SocialPlatform, value: string) {
  const trimmed = value.trim()
  if (platform === 'website') return trimmed
  return (
    trimmed
      .replace(/^@+/, '')
      .replace(/^https?:\/\/(www\.)?/i, '')
      .split('/')
      .filter(Boolean)
      .pop() ?? ''
  )
}

// ─── Table helpers ────────────────────────────────────────────────────────────

const columnHelper = createColumnHelper<ReferredUser>()

const timelines = [
  {title: 'All', value: 'all'},
  {title: 'This Month', value: 'last30'},
] as const

const EMPTY_REFERRED: ReferredUser[] = []

const referredColumns = [
  columnHelper.accessor('start_date', {
    header: 'Subscribed',
    cell: ({cell}) => {
      const value = cell.getValue()
      return value ? new Date(value).toDateString() : '—'
    },
  }),
  columnHelper.accessor('user_name', {header: 'User'}),
  columnHelper.accessor('is_trial', {
    header: 'Status',
    cell: ({cell}) => {
      const trial = cell.getValue()
      const color = trial ? '#F59E0B' : '#22C55E'
      return (
        <span
          style={{
            background: `${color}18`,
            color,
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 10px',
            borderRadius: 20,
          }}
        >
          {trial ? 'Trial' : 'Paid'}
        </span>
      )
    },
  }),
]
type QueryParams = {page: number; limit: number; order: SortOpts}

// ─── Share text builders ──────────────────────────────────────────────────────

const BU_HANDLE = '@bottomupsocial'
const TAGS = '#bottomUP #crypto #trading'
type TemplateKey =
  | 'performance'
  | 'invite'
  | 'setup_alert'
  | 'monthly_recap'
  | 'discipline'
  | 'community'

const SOCIAL_PLATFORMS: {
  key: SocialPlatform
  label: string
  icon: string
  color: string
  placeholder: string
  help: string
  buildUrl?: (value: string) => string
}[] = [
  {
    key: 'x',
    label: 'X',
    icon: '𝕏',
    color: '#1d9bf0',
    placeholder: 'username',
    help: 'Posts, trade cards, performance threads',
    buildUrl: (v) => `https://x.com/${v}`,
  },
  {
    key: 'telegram',
    label: 'Telegram',
    icon: '✈',
    color: '#2AABEE',
    placeholder: 'channel_or_username',
    help: 'Channel/community announcements',
    buildUrl: (v) => `https://t.me/${v}`,
  },
  {
    key: 'instagram',
    label: 'Instagram',
    icon: '◎',
    color: '#E1306C',
    placeholder: 'username',
    help: 'Stories and visual trade recaps',
    buildUrl: (v) => `https://instagram.com/${v}`,
  },
  {
    key: 'youtube',
    label: 'YouTube',
    icon: '▶',
    color: '#FF0000',
    placeholder: '@channel or channel URL',
    help: 'Long-form recaps and market reviews',
    buildUrl: (v) =>
      v.startsWith('youtube.com') || v.startsWith('www.youtube.com')
        ? `https://${v}`
        : `https://youtube.com/${v}`,
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    icon: '♪',
    color: '#25F4EE',
    placeholder: 'username',
    help: 'Short video setup summaries',
    buildUrl: (v) => `https://www.tiktok.com/@${v}`,
  },
  {
    key: 'discord',
    label: 'Discord',
    icon: '#',
    color: '#5865F2',
    placeholder: 'server invite or handle',
    help: 'Private communities and groups',
    buildUrl: (v) =>
      v.startsWith('discord.gg')
        ? `https://${v}`
        : v.startsWith('http')
        ? v
        : `https://discord.gg/${v}`,
  },
  {
    key: 'website',
    label: 'Website',
    icon: '↗',
    color: '#22C55E',
    placeholder: 'https://...',
    help: 'Your link hub, website, or newsletter',
    buildUrl: (v) => (v.startsWith('http') ? v : `https://${v}`),
  },
]

const SHARE_PLATFORMS: {key: SharePlatform; label: string; icon: string; color: string}[] = [
  {key: 'x', label: 'X', icon: '𝕏', color: '#1d9bf0'},
  {key: 'telegram', label: 'Telegram', icon: '✈', color: '#2AABEE'},
  {key: 'whatsapp', label: 'WhatsApp', icon: '☏', color: '#25D366'},
  {key: 'linkedin', label: 'LinkedIn', icon: 'in', color: '#0A66C2'},
  {key: 'facebook', label: 'Facebook', icon: 'f', color: '#1877F2'},
  {key: 'reddit', label: 'Reddit', icon: 'r/', color: '#FF4500'},
]

function buildStaticText(
  key: TemplateKey,
  opts: {referralCode: string; referralLink: string; stat?: string}
): string {
  switch (key) {
    case 'performance':
      return `My trading performance on ${BU_HANDLE}${
        opts.stat ? ` — ${opts.stat}` : ''
      }.\n\nFollow my setups and track the booked R results.\n${opts.referralLink}\n\n${TAGS}`
    case 'invite':
      return `I share my trade setups on ${BU_HANDLE} — entries, stops, targets and booked R.\n\nJoin free. The referral is applied automatically:\n${opts.referralLink}\n\n${TAGS}`
    case 'setup_alert':
      return `New setup published on ${BU_HANDLE}.\n\nEntry, stop and targets are on the card.\n${opts.referralLink}\n\n${TAGS}`
    case 'monthly_recap':
      return `Monthly recap is in.\n\nSetups and booked results are tracked on ${BU_HANDLE}.\n${opts.referralLink}\n\n${TAGS}`
    case 'discipline':
      return `Trading is the risk you keep, not the outcome you hope for.\n\nEvery setup is tracked on ${BU_HANDLE}.\n${opts.referralLink}\n\n${TAGS}`
    case 'community':
      return `Join the ${BU_HANDLE} community.\n\nSetups, stops and booked R, in the open.\n${opts.referralLink}\n\n${TAGS}`
  }
}

function buildTradeText(trade: ShareTrade, referralLink: string): string {
  const dir = trade.position === 'short' ? 'SHORT' : trade.position === 'long' ? 'LONG' : 'SETUP'
  const r = trade.realizedR != null ? formatR(trade.realizedR) : ''
  const tps = trade.tpHits.filter(Boolean).length
  const entry = trade.entry != null ? formatPrice(trade.entry) : ''
  const close = trade.close != null ? formatPrice(trade.close) : ''
  const date = trade.closedAt ? new Date(trade.closedAt).toLocaleDateString() : ''
  const lines = [
    `${trade.coin} ${dir}${r ? ` — ${r}` : ' — closed'}`,
    '',
    ...(entry ? [`Entry:  ${entry}`] : []),
    ...(close ? [`Close:  ${close}`] : []),
    ...(tps > 0 ? [`TPs hit: ${tps}`] : []),
    ...(date ? [`Date:   ${date}`] : []),
    '',
    `Tracked on ${BU_HANDLE}`,
    referralLink,
    '',
    TAGS,
  ]
  return lines.join('\n')
}

function openShareWindow(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer')
}

function shareOnX(text: string) {
  openShareWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`)
}

function shareOnTelegram(text: string, url: string) {
  openShareWindow(
    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
  )
}

function shareOnPlatform(platform: SharePlatform, text: string, url: string) {
  const encodedText = encodeURIComponent(text)
  const encodedUrl = encodeURIComponent(url)
  const shareUrls: Record<SharePlatform, string> = {
    x: `https://twitter.com/intent/tweet?text=${encodedText}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    whatsapp: `https://wa.me/?text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
  }
  openShareWindow(shareUrls[platform])
}

async function copyText(text: string, label = 'Post text') {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(`${label} copied`)
  } catch {
    toast.error('Failed to copy')
  }
}

async function nativeShare(text: string, url: string) {
  if (!navigator.share) {
    await copyText(text, 'Post text')
    toast.info('Native share is not available in this browser')
    return
  }
  try {
    await navigator.share({title: 'BottomUp trader update', text, url})
  } catch {}
}

// ─── Copy field ──────────────────────────────────────────────────────────────

function CopyField({label, value}: {label: string; value: string}) {
  return (
    <div>
      <p
        style={{
          color: 'var(--text-muted)',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          marginBottom: 6,
        }}
      >
        {label}
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--elevated-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 10,
          padding: '10px 12px',
        }}
      >
        <span
          style={{
            flex: 1,
            color: 'var(--text-secondary)',
            fontSize: 13,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </span>
        <CopyToClipboard text={value} onCopy={() => toast.success('Copied!')}>
          <button
            type='button'
            style={{
              flexShrink: 0,
              padding: '4px 10px',
              borderRadius: 7,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Copy
          </button>
        </CopyToClipboard>
      </div>
    </div>
  )
}

// ─── Image preview modal ──────────────────────────────────────────────────────

function ImageModal({
  imageUrl,
  coin,
  onClose,
}: {
  imageUrl: string
  coin: string
  onClose: () => void
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg)',
          borderRadius: 16,
          padding: 24,
          maxWidth: 660,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <p style={{color: 'var(--text-primary)', fontWeight: 700, fontSize: 15, margin: 0}}>
            Share Card — {coin}
          </p>
          <button
            type='button'
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <img
          src={imageUrl}
          alt='Trade card'
          style={{width: '100%', borderRadius: 10, display: 'block'}}
        />
        <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>
          Download the image and attach it to your X or Telegram post for maximum impact.
        </p>
        <div style={{display: 'flex', gap: 10}}>
          <a
            href={imageUrl}
            download={`${coin}-trade-bottomup.png`}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: 9,
              background: '#E84040',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            ↓ Download PNG
          </a>
          <button
            type='button'
            onClick={onClose}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: 9,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Trade share card (in-page component) ────────────────────────────────────

// Attempts to write a PNG blob to the system clipboard.
// Returns true if successful, false if the browser doesn't support it.
async function copyImageToClipboard(blob: Blob): Promise<boolean> {
  try {
    if (!navigator.clipboard || !('write' in navigator.clipboard)) return false
    await navigator.clipboard.write([new ClipboardItem({'image/png': blob})])
    return true
  } catch {
    return false
  }
}

const PNG_OPTS = {
  cacheBust: true,
  pixelRatio: 1,
  backgroundColor: '#0d0e12',
}

function TradeShareCard({
  trade,
  referralLink,
  traderName,
}: {
  trade: ShareTrade
  referralLink: string
  traderName: string
}) {
  const [preview, setPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loadingPlatform, setLoadingPlatform] = useState<SharePlatform | 'download' | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const text = buildTradeText(trade, referralLink)

  // Generate image, copy to clipboard, open share URL
  const handleShare = useCallback(
    async (platform: SharePlatform) => {
      if (!cardRef.current || loadingPlatform) return
      setLoadingPlatform(platform)
      try {
        // Generate both blob (for clipboard) and data URL (for modal preview)
        const [blob, url] = await Promise.all([
          toBlob(cardRef.current, PNG_OPTS),
          toPng(cardRef.current, PNG_OPTS),
        ])

        // Store preview URL so user can also download
        if (url) setPreviewUrl(url)

        // Try copying image to clipboard
        const copied = blob ? await copyImageToClipboard(blob) : false

        // Open the share URL
        shareOnPlatform(platform, text, referralLink)

        // Feedback toast
        if (copied) {
          toast.success('Image copied to clipboard — paste it into your post! (Ctrl+V / ⌘V)', {
            autoClose: 5000,
          })
        } else {
          toast.info('Image generated — download it and attach to your post.', {autoClose: 4000})
        }
      } catch {
        toast.error('Failed to generate image')
      } finally {
        setLoadingPlatform(null)
      }
    },
    [loadingPlatform, text, referralLink]
  )

  // Download image without sharing
  const handleDownload = useCallback(async () => {
    if (!cardRef.current || loadingPlatform) return
    setLoadingPlatform('download')
    try {
      const url = await toPng(cardRef.current, PNG_OPTS)
      setPreviewUrl(url)
    } catch {
      toast.error('Failed to generate image')
    } finally {
      setLoadingPlatform(null)
    }
  }, [loadingPlatform])

  const busy = loadingPlatform !== null

  return (
    <>
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          transform: 'translateX(-120vw)',
          pointerEvents: 'none',
        }}
      >
        <div ref={cardRef}>
          <TradePoster trade={trade} traderName={traderName} />
        </div>
      </div>
      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        <PosterFrame>
          <TradePoster trade={trade} traderName={traderName} />
        </PosterFrame>
        <div style={{padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 14}}>

          {/* Text preview toggle */}
          <button
            type='button'
            onClick={() => setPreview((p) => !p)}
            style={{
              alignSelf: 'flex-start',
              padding: '4px 10px',
              borderRadius: 7,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {preview ? 'Hide post text' : 'Preview post text'}
          </button>
          {preview && (
            <div
              style={{
                background: 'var(--elevated-bg)',
                borderRadius: 10,
                padding: '12px 14px',
                color: 'var(--text-secondary)',
                fontSize: 13,
                lineHeight: 1.65,
                whiteSpace: 'pre-line',
                borderLeft: '3px solid #E84040',
              }}
            >
              {text}
            </div>
          )}

          {/* Share buttons — image is auto-generated + copied on click */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8}}>
            {SHARE_PLATFORMS.slice(0, 6).map((platform) => (
              <button
                key={platform.key}
                type='button'
                onClick={() => handleShare(platform.key)}
                disabled={busy}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '9px 6px',
                  borderRadius: 9,
                  border: `1px solid ${platform.color}33`,
                  background:
                    loadingPlatform === platform.key ? 'var(--elevated-bg)' : `${platform.color}10`,
                  color: loadingPlatform === platform.key ? 'var(--text-muted)' : platform.color,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: busy ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {loadingPlatform === platform.key ? (
                  '⏳'
                ) : (
                  <span style={{fontSize: 12}}>{platform.icon}</span>
                )}
                {loadingPlatform === platform.key ? 'Prep…' : platform.label}
              </button>
            ))}
          </div>

          {/* Download image only */}
          <div style={{display: 'flex', gap: 8}}>
            <button
              type='button'
              onClick={() => copyText(text)}
              disabled={busy}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '7px',
                borderRadius: 8,
                border: '1px solid var(--card-border)',
                background: 'transparent',
                color: 'var(--text-muted)',
                fontSize: 11,
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              Copy text
            </button>
            <button
              type='button'
              onClick={() => nativeShare(text, referralLink)}
              disabled={busy}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '7px',
                borderRadius: 8,
                border: '1px solid var(--card-border)',
                background: 'transparent',
                color: 'var(--text-muted)',
                fontSize: 11,
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              Native share
            </button>
            <button
              type='button'
              onClick={handleDownload}
              disabled={busy}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '7px',
                borderRadius: 8,
                border: '1px solid var(--card-border)',
                background: 'transparent',
                color: 'var(--text-muted)',
                fontSize: 11,
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {loadingPlatform === 'download' ? '⏳ Generating…' : '↓ Image'}
            </button>
          </div>
        </div>
      </div>

      {/* Image preview modal */}
      {previewUrl && (
        <ImageModal imageUrl={previewUrl} coin={trade.coin} onClose={() => setPreviewUrl(null)} />
      )}
    </>
  )
}

// ─── Static share card ────────────────────────────────────────────────────────

function StaticShareCard({
  title,
  description,
  text,
  link,
}: {
  title: string
  description: string
  text: string
  link: string
}) {
  const [preview, setPreview] = useState(false)
  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <div style={{height: 3, background: '#E84040'}} />
      <div style={{padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
          <div style={{minWidth: 0, flex: 1}}>
            <p style={{color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, margin: 0}}>
              {title}
            </p>
            <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>{description}</p>
          </div>
          <button
            type='button'
            onClick={() => setPreview((p) => !p)}
            style={{
              flexShrink: 0,
              padding: '4px 10px',
              borderRadius: 7,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {preview ? 'Hide' : 'Preview'}
          </button>
        </div>
        {preview && (
          <div
            style={{
              background: 'var(--elevated-bg)',
              borderRadius: 10,
              padding: '12px 14px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              lineHeight: 1.65,
              whiteSpace: 'pre-line',
              borderLeft: '3px solid #E84040',
            }}
          >
            {text}
          </div>
        )}
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8}}>
          {SHARE_PLATFORMS.map((platform) => (
            <button
              key={platform.key}
              type='button'
              onClick={() => shareOnPlatform(platform.key, text, link)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '8px 6px',
                borderRadius: 9,
                border: `1px solid ${platform.color}33`,
                background: `${platform.color}10`,
                color: platform.color,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <span style={{fontSize: 12}}>{platform.icon}</span> {platform.label}
            </button>
          ))}
        </div>
        <div style={{display: 'flex', gap: 8}}>
          <button
            type='button'
            onClick={() => copyText(text)}
            style={{
              flex: 1,
              padding: '8px',
              borderRadius: 8,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Copy post
          </button>
          <button
            type='button'
            onClick={() => nativeShare(text, link)}
            style={{
              flex: 1,
              padding: '8px',
              borderRadius: 8,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Native share
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Connect account card ─────────────────────────────────────────────────────

function ConnectAccountCard({
  platform,
  icon,
  accentColor,
  bgColor,
  placeholder,
  help,
  value,
  onChange,
  profileUrl,
}: {
  platform: string
  icon: React.ReactNode
  accentColor: string
  bgColor: string
  placeholder: string
  help: string
  value: string
  onChange: (v: string) => void
  profileUrl?: string
}) {
  const connected = value.trim().length > 0
  return (
    <div
      style={{
        background: 'var(--elevated-bg)',
        border: `1px solid ${connected ? `${accentColor}44` : 'var(--card-border)'}`,
        borderRadius: 12,
        padding: '16px 18px',
        transition: 'border-color 0.2s',
      }}
    >
      <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14}}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 9,
            background: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{flex: 1, minWidth: 0}}>
          <p style={{color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, margin: 0}}>
            {platform}
          </p>
          {connected ? (
            <p style={{color: accentColor, fontSize: 12, margin: 0}}>@{value}</p>
          ) : (
            <p style={{color: 'var(--text-faint, #666)', fontSize: 12, margin: 0}}>Not connected</p>
          )}
        </div>
        <span
          style={{
            padding: '4px 9px',
            borderRadius: 999,
            background: connected ? `${accentColor}18` : 'var(--card-bg)',
            border: `1px solid ${connected ? `${accentColor}44` : 'var(--card-border)'}`,
            color: connected ? accentColor : 'var(--text-faint, #666)',
            fontSize: 10,
            fontWeight: 800,
            textTransform: 'uppercase',
          }}
        >
          {connected ? 'Active' : 'Draft'}
        </span>
      </div>
      <p style={{color: 'var(--text-muted)', fontSize: 11, margin: '0 0 10px'}}>{help}</p>
      <div style={{display: 'flex', gap: 8}}>
        <input
          type='text'
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 9,
            padding: '9px 13px',
            color: 'var(--text-primary)',
            fontSize: 13,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {connected && (
          <button
            type='button'
            onClick={() => onChange('')}
            style={{
              padding: '0 10px',
              borderRadius: 9,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
      </div>
      {connected && profileUrl && (
        <div style={{display: 'flex', gap: 8, marginTop: 10}}>
          <CopyToClipboard
            text={profileUrl}
            onCopy={() => toast.success(`${platform} link copied`)}
          >
            <button
              type='button'
              style={{
                flex: 1,
                padding: '7px',
                borderRadius: 8,
                border: `1px solid ${accentColor}33`,
                background: `${accentColor}10`,
                color: accentColor,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Copy link
            </button>
          </CopyToClipboard>
          <a
            href={profileUrl}
            target='_blank'
            rel='noreferrer'
            style={{
              flex: 1,
              padding: '7px',
              borderRadius: 8,
              border: `1px solid ${accentColor}33`,
              background: 'transparent',
              color: accentColor,
              fontSize: 11,
              fontWeight: 700,
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            Open ↗
          </a>
        </div>
      )}
    </div>
  )
}

function ProfileCard({
  traderName,
  stats,
  rank,
  totalTraders,
  referralLink,
}: {
  traderName: string
  stats: any
  rank: number | null
  totalTraders: number
  referralLink: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)

  const netR = Number(stats?.net_r ?? 0)
  const wr = Number(stats?.winrate ?? 0)
  const wins = Number(stats?.success ?? 0)
  const losses = Number(stats?.stopped ?? 0)

  const shareText = `My monthly trading performance on @bottomupsocial:\n\nNet R: ${formatR(
    netR
  )}\nWin Rate: ${wr.toFixed(1)}%\nTrades: ${wins + losses} (${wins}W/${losses}L)${
    rank ? `\nRank: #${rank}` : ''
  }\n\nBooked on bottomUP.\n${referralLink}\n\n#bottomUP #crypto #trading`

  const handleExport = useCallback(
    async (action: 'download' | 'x' | 'telegram') => {
      if (!cardRef.current || loading) return
      setLoading(action)
      try {
        const [blob, url] = await Promise.all([
          toBlob(cardRef.current, PNG_OPTS),
          toPng(cardRef.current, PNG_OPTS),
        ])
        setPreviewUrl(url)

        if (action === 'download') {
          const a = document.createElement('a')
          a.href = url
          a.download = `${traderName}-performance-bottomup.png`
          a.click()
          toast.success('Image downloaded')
        } else {
          if (blob) {
            try {
              await navigator.clipboard.write([new ClipboardItem({'image/png': blob})])
              toast.success('Image copied to clipboard — paste it into your post!', {
                autoClose: 5000,
              })
            } catch {
              toast.info('Image generated — download and attach to your post.', {autoClose: 4000})
            }
          }
          if (action === 'x') shareOnX(shareText)
          else shareOnTelegram(shareText, referralLink)
        }
      } catch {
        toast.error('Failed to generate image')
      } finally {
        setLoading(null)
      }
    },
    [loading, traderName, shareText, referralLink]
  )

  if (!stats) {
    return (
      <div
        style={{
          background: 'var(--elevated-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 12,
          padding: '32px',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 13,
        }}
      >
        No performance data available yet. Start trading to generate your card.
      </div>
    )
  }

  return (
    <>
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          transform: 'translateX(-120vw)',
          pointerEvents: 'none',
        }}
      >
        <div ref={cardRef}>
          <PerformancePoster
            traderName={traderName}
            netR={netR}
            winRate={wr}
            wins={wins}
            losses={losses}
            rank={rank}
            totalTraders={totalTraders}
          />
        </div>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
        <PosterFrame>
          <PerformancePoster
            traderName={traderName}
            netR={netR}
            winRate={wr}
            wins={wins}
            losses={losses}
            rank={rank}
            totalTraders={totalTraders}
          />
        </PosterFrame>

        {/* Actions */}
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8}}>
          <button
            type='button'
            onClick={() => handleExport('download')}
            disabled={!!loading}
            style={{
              padding: '10px',
              borderRadius: 9,
              border: '1px solid var(--card-border)',
              background: 'var(--elevated-bg)',
              color: 'var(--text-secondary)',
              fontWeight: 700,
              fontSize: 12,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading === 'download' ? 'Generating...' : 'Download PNG'}
          </button>
          <button
            type='button'
            onClick={() => handleExport('x')}
            disabled={!!loading}
            style={{
              padding: '10px',
              borderRadius: 9,
              border: '1px solid #1d9bf033',
              background: '#1d9bf010',
              color: '#1d9bf0',
              fontWeight: 700,
              fontSize: 12,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading === 'x' ? 'Preparing...' : 'Share on X'}
          </button>
          <button
            type='button'
            onClick={() => handleExport('telegram')}
            disabled={!!loading}
            style={{
              padding: '10px',
              borderRadius: 9,
              border: '1px solid #2AABEE33',
              background: '#2AABEE10',
              color: '#2AABEE',
              fontWeight: 700,
              fontSize: 12,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading === 'telegram' ? 'Preparing...' : 'Share on Telegram'}
          </button>
          <button
            type='button'
            onClick={() => shareOnPlatform('linkedin', shareText, referralLink)}
            style={{
              padding: '10px',
              borderRadius: 9,
              border: '1px solid #0A66C233',
              background: '#0A66C210',
              color: '#0A66C2',
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            LinkedIn
          </button>
          <button
            type='button'
            onClick={() => copyText(shareText)}
            style={{
              padding: '10px',
              borderRadius: 9,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Copy post
          </button>
          <button
            type='button'
            onClick={() => nativeShare(shareText, referralLink)}
            style={{
              padding: '10px',
              borderRadius: 9,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-muted)',
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Native share
          </button>
        </div>
      </div>

      {previewUrl && (
        <ImageModal imageUrl={previewUrl} coin={traderName} onClose={() => setPreviewUrl(null)} />
      )}
    </>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function Referral() {
  const navigate = useNavigate()
  const {currentUser} = useAuth()

  const [activeTab, setActiveTab] = useState<'leaderboard' | 'share' | 'profile' | 'referral'>(
    'leaderboard'
  )
  const [handles, setHandles] = useState(loadHandles)
  const [draftHandles, setDraftHandles] = useState<SocialHandles>(handles)
  const [selectedTime, setSelectedTime] = useState<DayFilter>('all')
  const qrRef = useRef<HTMLDivElement>(null)
  const currPage = Number(new URLSearchParams(window.location.search).get('page')) || 1

  const getQueryParams = (): QueryParams => {
    const s = new URLSearchParams(window.location.search)
    return {
      page: Number(s.get('page')) || 1,
      limit: Number(s.get('limit')) || 10,
      order: (s.get('order') as SortOpts) || 'newest',
    }
  }

  const getQueryString = (params: QueryParams): string => {
    const s = new URLSearchParams()
    if (params.page !== 1) s.append('page', params.page.toString())
    if (params.limit !== 10) s.append('limit', params.limit.toString())
    if (params.order !== 'newest') s.append('order', params.order)
    return s.toString()
  }

  const {data: closedSetups = []} = useQuery(
    ['social-closed-setups', currentUser?.id],
    async () => {
      const res = await setupList({
        userId: currentUser?.id,
        limit: 50,
        skip: 0,
        config: {_extraParams: {state: 'archived'}} as any,
      })
      return res.data.data.filter((s) => ['success', 'stopped', 'closed'].includes(s.status))
    },
    {enabled: !!currentUser?.id}
  )

  const {data: bookRows = []} = useQuery(
    ['social-book-r', currentUser?.name],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-latest-setup'), {
        params: {
          analyst_name: currentUser?.name,
          target_date: 'this_year',
          skip: 0,
          limit: 100,
        },
      })
      return (res.data?.data ?? []) as BookRow[]
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const closedTrades = useMemo(
    () => buildShareTrades(closedSetups as SetupLike[], bookRows),
    [bookRows, closedSetups]
  )

  const {data: leaderboardData, isLoading: lbLoading} = useQuery(
    ['social-leaderboard'],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-leaderboard'), {
        params: {
          target_date: 'this_month',
          skip: 0,
          limit: 100,
          order_by: 'net_r',
          order_direction: 'desc',
        },
      })
      return {rows: res.data?.data ?? [], total: res.data?.total_count ?? 0}
    },
    {refetchOnWindowFocus: false}
  )

  const {data: myStats} = useQuery(
    ['social-my-stats', currentUser?.name],
    async () => {
      const res = await axios.get(apiURL('/analytic/futures-leaderboard'), {
        params: {analyst_name: currentUser?.name, target_date: 'this_month', skip: 0, limit: 1},
      })
      return res.data?.data?.[0] ?? null
    },
    {enabled: Boolean(currentUser?.name), refetchOnWindowFocus: false}
  )

  const myRank = useMemo(() => {
    if (!leaderboardData?.rows || !currentUser?.name) return null
    const idx = leaderboardData.rows.findIndex((r: any) => r.analysts === currentUser?.name)
    return idx >= 0 ? idx + 1 : null
  }, [leaderboardData, currentUser?.name])

  const {data: referralCode = ''} = useQuery(
    'referral-code',
    async () => (await getReferralCode()).data.code
  )
  const {data: referralSummary} = useQuery(['referral-summary', selectedTime], async () =>
    (await getReferralSummary(selectedTime)).data
  )
  const referredList = useQuery(['referred-users', selectedTime, currPage], async () => {
    const params = getQueryParams()
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    const pad = (n: number) => String(n).padStart(2, '0')
    const from_date = selectedTime === 'last30' ? `${y}-${pad(m + 1)}-01` : undefined
    const to_date =
      selectedTime === 'last30'
        ? m === 11
          ? `${y + 1}-01-01`
          : `${y}-${pad(m + 2)}-01`
        : undefined
    return (
      await getReferredList({
        from_date,
        to_date,
        limit: Math.max(params.limit, 50),
        skip: (params.page - 1) * Math.max(params.limit, 50),
      })
    ).data
  })
  // Analyst pages resolve by User.name (`/analyst/:name`), not referral_code.
  // Apex host only: www.bottomup.app 308s to webapp.* which 404s.
  const analystSlug = (currentUser?.name || '').trim()
  const referralLink = analystSlug
    ? `https://bottomup.app/analyst/${encodeURIComponent(analystSlug)}`
    : ''
  const traderName = currentUser?.name || 'Trader'

  const {getHeaderGroups, getRowModel} = useReactTable<ReferredUser>({
    data: referredList?.data?.data ?? EMPTY_REFERRED,
    columns: referredColumns,
    sortDescFirst: true,
    getCoreRowModel: getCoreRowModel<ReferredUser>(),
    getSortedRowModel: getSortedRowModel<ReferredUser>(),
  })

  const handlePageChange = (page: number) => {
    const params = getQueryParams()
    params.page = Number(page)
    navigate({pathname: '/referral', search: getQueryString(params)})
  }

  const saveAccountHandles = () => {
    const next = SOCIAL_PLATFORMS.reduce(
      (acc, platform) => {
        acc[platform.key] = cleanHandle(platform.key, draftHandles[platform.key])
        return acc
      },
      {...EMPTY_HANDLES}
    )
    setHandles(next)
    setDraftHandles(next)
    saveHandles(next)
    toast.success('Accounts saved')
  }

  const resetAccountHandles = () => {
    setDraftHandles(handles)
    toast.info('Account drafts reset')
  }

  const downloadQRCode = () => {
    if (qrRef.current) {
      toJpeg(qrRef.current, {cacheBust: true})
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = 'QRCode.jpeg'
          link.href = dataUrl
          link.click()
        })
        .catch(() => toast.error('Failed to download QRCode'))
    }
  }

  const connectedCount = SOCIAL_PLATFORMS.filter((platform) => handles[platform.key]).length

  const CARD: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: 14,
    padding: '20px 24px',
  }
  const LABEL: React.CSSProperties = {
    color: 'var(--text-muted)',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    display: 'block',
    marginBottom: 14,
  }

  const staticTemplates: {
    title: string
    description: string
    key: TemplateKey
  }[] = [
    {
      title: 'New setup',
      description: 'Tell followers a setup is live. The card carries the levels.',
      key: 'setup_alert',
    },
    {
      title: 'Performance',
      description: 'Post this month’s booked net R and win rate.',
      key: 'performance',
    },
    {
      title: 'Monthly recap',
      description: 'Point people at the month’s tracked results.',
      key: 'monthly_recap',
    },
    {
      title: 'Discipline',
      description: 'Talk about the risk you kept, not a lucky outcome.',
      key: 'discipline',
    },
    {
      title: 'Community',
      description: 'Invite people into the tracked setup feed.',
      key: 'community',
    },
    {
      title: 'Referral',
      description: 'Share the analyst link that applies your code.',
      key: 'invite',
    },
  ]

  const tabBtn = (id: typeof activeTab, label: string) => (
    <button
      type='button'
      onClick={() => setActiveTab(id)}
      style={{
        padding: '7px 20px',
        borderRadius: 8,
        border: 'none',
        fontWeight: 700,
        fontSize: 13,
        cursor: 'pointer',
        transition: 'all 0.15s',
        background: activeTab === id ? 'var(--text-primary)' : 'transparent',
        color: activeTab === id ? 'var(--page-bg, #0d0e12)' : 'var(--text-muted)',
      }}
    >
      {label}
    </button>
  )

  return (
    <div style={{maxWidth: 960, margin: '0 auto', paddingBottom: 56}}>
      {/* Page header */}
      <div
        style={{
          ...CARD,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, margin: '0 0 4px'}}
          >
            Social
          </h1>
          <p style={{color: 'var(--text-muted)', fontSize: 13, margin: 0}}>
            Share your achievements and grow your audience.
          </p>
        </div>
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
          {SOCIAL_PLATFORMS.slice(0, 5).map(({key, label, icon, color}) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 20,
                background: handles[key] ? `${color}18` : 'var(--elevated-bg)',
                border: `1px solid ${handles[key] ? `${color}44` : 'var(--card-border)'}`,
              }}
            >
              <span style={{fontSize: 12}}>{icon}</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: handles[key] ? color : 'var(--text-faint, #666)',
                }}
              >
                {handles[key]
                  ? key === 'website'
                    ? 'Website saved'
                    : `@${handles[key]}`
                  : `${label} not connected`}
              </span>
            </div>
          ))}
          {connectedCount > 0 && (
            <div
              style={{
                padding: '6px 12px',
                borderRadius: 20,
                background: '#22C55E18',
                border: '1px solid #22C55E44',
                fontSize: 12,
                fontWeight: 700,
                color: '#22C55E',
              }}
            >
              {connectedCount} connected
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'inline-flex',
          gap: 4,
          marginBottom: 20,
          background: 'var(--elevated-bg)',
          borderRadius: 10,
          padding: 4,
          border: '1px solid var(--card-border)',
        }}
      >
        {tabBtn('leaderboard', 'Leaderboard')}
        {tabBtn('share', 'Share')}
        {tabBtn('profile', 'Profile Card')}
        {tabBtn('referral', 'Referral')}
      </div>

      {/* ═══════════ LEADERBOARD TAB ═══════════ */}
      {activeTab === 'leaderboard' && (
        <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
          <div style={CARD}>
            <span style={LABEL}>Monthly Leaderboard</span>
            <p style={{color: 'var(--text-muted)', fontSize: 12, marginTop: 0, marginBottom: 16}}>
              Top traders ranked by net R this month.
            </p>
            {lbLoading ? (
              <Loading className='py-10' />
            ) : !leaderboardData?.rows?.length ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 0',
                  color: 'var(--text-faint, #666)',
                  fontSize: 14,
                }}
              >
                No leaderboard data available yet.
              </div>
            ) : (
              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '1px solid var(--divider)'}}>
                      {['#', 'Trader', 'Net R', 'Win Rate', 'Wins', 'Losses', 'Trades'].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: '10px 12px',
                            textAlign: h === 'Trader' ? 'left' : 'center',
                            color: 'var(--text-muted)',
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardData.rows.slice(0, 20).map((row: any, idx: number) => {
                      const isMe = row.analysts === currentUser?.name
                      return (
                        <tr
                          key={row.analysts ?? idx}
                          style={{
                            borderBottom: '1px solid var(--divider)',
                            background: isMe ? 'rgba(129,140,248,0.06)' : 'transparent',
                          }}
                        >
                          <td
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontWeight: 800,
                              fontSize: 14,
                              color: idx < 3 ? '#F59E0B' : 'var(--text-secondary)',
                            }}
                          >
                            {idx + 1}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              fontWeight: 600,
                              fontSize: 13,
                              color: isMe ? '#818cf8' : 'var(--text-primary)',
                            }}
                          >
                            {row.analysts || 'Unknown'}
                            {isMe && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: '#818cf8',
                                  background: '#818cf818',
                                  padding: '1px 6px',
                                  borderRadius: 4,
                                }}
                              >
                                YOU
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontWeight: 700,
                              fontSize: 13,
                              color: Number(row.net_r ?? 0) >= 0 ? '#22C55E' : '#E84040',
                            }}
                          >
                            {Number(row.net_r ?? 0) >= 0 ? '+' : ''}
                            {Number(row.net_r ?? 0).toFixed(2)}R
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: 13,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {Number(row.winrate ?? 0).toFixed(1)}%
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: 13,
                              color: '#22C55E',
                            }}
                          >
                            {row.success ?? 0}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: 13,
                              color: '#E84040',
                            }}
                          >
                            {row.stopped ?? 0}
                          </td>
                          <td
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: 13,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {Number(row.success ?? 0) + Number(row.stopped ?? 0)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ SHARE TAB ═══════════ */}
      {activeTab === 'share' && (
        <div style={{display: 'flex', flexDirection: 'column', gap: 24}}>
          {/* Manage accounts */}
          <div style={CARD}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
                marginBottom: 14,
              }}
            >
              <div>
                <span style={{...LABEL, marginBottom: 6}}>Trader Social Hub</span>
                <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>
                  Manage the accounts you use for distribution without leaving BottomUp. Save once,
                  copy links, and reuse them in every share card.
                </p>
              </div>
              <div
                style={{
                  padding: '7px 12px',
                  borderRadius: 999,
                  background: '#818cf818',
                  border: '1px solid #818cf844',
                  color: '#818cf8',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {connectedCount}/{SOCIAL_PLATFORMS.length} channels active
              </div>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 14}}>
              {SOCIAL_PLATFORMS.map((platform) => {
                const value = draftHandles[platform.key]
                const cleaned = cleanHandle(platform.key, value)
                return (
                  <ConnectAccountCard
                    key={platform.key}
                    platform={platform.label}
                    icon={
                      <span style={{color: '#fff', fontWeight: 800, fontSize: 14}}>
                        {platform.icon}
                      </span>
                    }
                    accentColor={platform.color}
                    bgColor={platform.color}
                    placeholder={platform.placeholder}
                    help={platform.help}
                    value={value}
                    onChange={(next) =>
                      setDraftHandles((prev) => ({...prev, [platform.key]: next}))
                    }
                    profileUrl={
                      cleaned && platform.buildUrl ? platform.buildUrl(cleaned) : undefined
                    }
                  />
                )
              })}
            </div>
            <div style={{display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap'}}>
              <button
                type='button'
                onClick={saveAccountHandles}
                style={{
                  padding: '10px 24px',
                  borderRadius: 9,
                  border: 'none',
                  background: '#E84040',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Save Social Hub
              </button>
              <button
                type='button'
                onClick={resetAccountHandles}
                style={{
                  padding: '10px 18px',
                  borderRadius: 9,
                  border: '1px solid var(--card-border)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Reset Drafts
              </button>
              <button
                type='button'
                onClick={() =>
                  copyText(
                    SOCIAL_PLATFORMS.filter((p) => handles[p.key])
                      .map(
                        (p) =>
                          `${p.label}: ${p.buildUrl ? p.buildUrl(handles[p.key]) : handles[p.key]}`
                      )
                      .join('\n'),
                    'Social links'
                  )
                }
                style={{
                  padding: '10px 18px',
                  borderRadius: 9,
                  border: '1px solid var(--card-border)',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Copy saved links
              </button>
            </div>
          </div>

          {/* Content planner */}
          <div style={CARD}>
            <span style={{...LABEL, marginBottom: 6}}>Content Planner</span>
            <p style={{color: 'var(--text-muted)', fontSize: 12, marginTop: 0, marginBottom: 14}}>
              A simple publishing board for deciding what to post next. Use it as an in-app social
              checklist; no platform redirect required.
            </p>
            <div className='grid grid-cols-1 sm:grid-cols-3' style={{gap: 12}}>
              {[
                {
                  title: 'Today',
                  items: [
                    'Share best closed setup',
                    'Post one risk lesson',
                    'Reply to community questions',
                  ],
                  color: '#22C55E',
                },
                {
                  title: 'This Week',
                  items: [
                    'Publish performance card',
                    'Invite followers to BottomUp',
                    'Pin referral link in bio',
                  ],
                  color: '#818cf8',
                },
                {
                  title: 'Evergreen',
                  items: [
                    'Trading discipline thread',
                    'Setup process explainer',
                    'Monthly recap template',
                  ],
                  color: '#F59E0B',
                },
              ].map((group) => (
                <div
                  key={group.title}
                  style={{
                    background: 'var(--elevated-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 12,
                    padding: 16,
                    borderTop: `3px solid ${group.color}`,
                  }}
                >
                  <p
                    style={{
                      color: 'var(--text-primary)',
                      fontSize: 13,
                      fontWeight: 800,
                      margin: '0 0 10px',
                    }}
                  >
                    {group.title}
                  </p>
                  <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                    {group.items.map((item) => (
                      <label
                        key={item}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: 'var(--text-secondary)',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        <input type='checkbox' style={{accentColor: group.color}} />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Share closed trades */}
          <div>
            <span style={{...LABEL, marginBottom: 6}}>Share a Closed Trade</span>
            <p style={{color: 'var(--text-muted)', fontSize: 12, marginBottom: 14, marginTop: 0}}>
              Generate a branded image card for any closed trade and share it on X or Telegram.
            </p>
            {closedTrades.length === 0 ? (
              <div
                style={{
                  background: 'var(--elevated-bg)',
                  border: '1px solid var(--card-border)',
                  borderRadius: 12,
                  padding: '32px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                No closed trades yet. Close a setup and it will appear here.
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 14}}>
                {closedTrades.map((trade) => (
                  <TradeShareCard
                    key={trade.id}
                    trade={trade}
                    referralLink={referralLink}
                    traderName={traderName}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Static templates */}
          <div>
            <span style={{...LABEL, marginBottom: 6}}>General Templates</span>
            <p style={{color: 'var(--text-muted)', fontSize: 12, marginBottom: 14, marginTop: 0}}>
              Ready-to-use posts for any occasion. Click "Preview" to see the full text before
              sharing.
            </p>
            <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 14}}>
              {staticTemplates.map((t) => (
                <StaticShareCard
                  key={t.key}
                  title={t.title}
                  description={t.description}
                  link={referralLink}
                  text={buildStaticText(t.key, {
                    referralCode,
                    referralLink,
                    stat:
                      t.key === 'performance' && myStats
                        ? `${formatR(Number(myStats.net_r ?? 0))} net, ${Number(
                            myStats.winrate ?? 0
                          ).toFixed(1)}% win rate`
                        : undefined,
                  })}
                />
              ))}
            </div>
          </div>

          {/* Follow bottomUP */}
          <div
            style={{
              background: 'var(--elevated-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: 14,
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <p
                style={{
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontWeight: 700,
                  margin: '0 0 2px',
                }}
              >
                Follow bottomUP on social
              </p>
              <p style={{color: 'var(--text-muted)', fontSize: 12, margin: 0}}>
                Stay up to date with market insights and platform updates.
              </p>
            </div>
            <a
              href='https://twitter.com/bottomupsocial'
              target='_blank'
              rel='noreferrer'
              style={{
                padding: '8px 16px',
                borderRadius: 9,
                border: '1px solid #1d9bf033',
                background: '#1d9bf010',
                color: '#1d9bf0',
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <span>𝕏</span> @bottomupsocial
            </a>
          </div>
        </div>
      )}

      {/* ═══════════ PROFILE CARD TAB ═══════════ */}
      {activeTab === 'profile' && (
        <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
          <div style={CARD}>
            <span style={LABEL}>Your Performance Card</span>
            <p style={{color: 'var(--text-muted)', fontSize: 12, marginTop: 0, marginBottom: 20}}>
              A shareable snapshot of your monthly trading performance. Download as an image or
              share directly.
            </p>
            <ProfileCard
              traderName={traderName}
              stats={myStats}
              rank={myRank}
              totalTraders={leaderboardData?.total ?? 0}
              referralLink={referralLink}
            />
          </div>
        </div>
      )}

      {/* ═══════════ REFERRAL TAB ═══════════ */}
      {activeTab === 'referral' && (
        <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
          <div style={CARD}>
            <span style={LABEL}>Your Referral Link</span>
            <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 14, marginBottom: 20}}>
              <CopyField label='Referral Code' value={referralCode} />
              <CopyField label='Referral Link' value={referralLink} />
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'}}>
              <div
                ref={qrRef}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <img
                  src={toAbsoluteUrl('/media/logos/logo-main.png')}
                  alt='bottomUP'
                  style={{height: 28}}
                />
                <QRCodeSVG value={referralLink || 'https://bottomup.app'} size={96} />
                <p style={{color: '#111', fontSize: 11, fontWeight: 700, margin: 0}}>
                  {referralCode}
                </p>
              </div>
              <button
                type='button'
                onClick={downloadQRCode}
                style={{
                  padding: '10px 22px',
                  borderRadius: 9,
                  border: 'none',
                  background: '#E84040',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Download QR
              </button>
            </div>
          </div>

          <div style={CARD}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span style={LABEL}>Dashboard</span>
              <div style={{display: 'flex', gap: 6}}>
                {timelines.map((item) => (
                  <button
                    key={item.value}
                    type='button'
                    onClick={() => {
                      setSelectedTime(item.value)
                      if (currPage !== 1) {
                        const params = getQueryParams()
                        params.page = 1
                        navigate({pathname: '/referral', search: getQueryString(params)})
                      }
                    }}
                    style={{
                      padding: '5px 14px',
                      borderRadius: 7,
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                      background:
                        selectedTime === item.value ? 'var(--text-primary)' : 'var(--elevated-bg)',
                      color:
                        selectedTime === item.value
                          ? 'var(--page-bg, #0d0e12)'
                          : 'var(--text-muted)',
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2' style={{gap: 12}}>
              {[
                {
                  label: 'Your Earnings',
                  value: referralSummary?.total_amount ?? '—',
                  unit: 'USD',
                  color: '#22C55E',
                },
                {
                  label: 'Subscribers',
                  value: referredList?.data?.total_count ?? '—',
                  unit: 'users',
                  color: '#818cf8',
                },
                {
                  label: 'Trial',
                  value: referredList?.data?.trial_count ?? '—',
                  unit: 'users',
                  color: '#F59E0B',
                },
                {
                  label: 'Paid',
                  value: referredList?.data?.paid_count ?? '—',
                  unit: 'users',
                  color: '#22C55E',
                },
              ].map(({label, value, unit, color}) => (
                <div
                  key={label}
                  style={{
                    background: 'var(--elevated-bg)',
                    borderRadius: 12,
                    padding: '16px 20px',
                    borderLeft: `3px solid ${color}`,
                  }}
                >
                  <p
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      margin: '0 0 8px',
                    }}
                  >
                    {label}
                  </p>
                  <p
                    style={{color: 'var(--text-primary)', fontSize: 26, fontWeight: 700, margin: 0}}
                  >
                    {value}{' '}
                    <span style={{color: 'var(--text-muted)', fontSize: 14, fontWeight: 400}}>
                      {unit}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div style={CARD}>
            <span style={LABEL}>My Subscribers</span>
            <div style={{position: 'relative', overflowX: 'auto'}}>
              {(referredList?.isLoading || referredList?.isRefetching) && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'var(--card-bg)',
                    opacity: 0.8,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Loading className='text-md font-medium' />
                </div>
              )}
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  {getHeaderGroups().map((hg) => (
                    <tr key={hg.id} style={{borderBottom: '1px solid var(--divider)'}}>
                      {hg.headers.map((h) => (
                        <th
                          key={h.id}
                          style={{
                            padding: '10px 12px',
                            textAlign: 'left',
                            color: 'var(--text-muted)',
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.07em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {getRowModel().rows.length > 0 ? (
                    getRowModel().rows.map((row) => (
                      <tr key={row.id} style={{borderBottom: '1px solid var(--divider)'}}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{
                              padding: '12px',
                              color: 'var(--text-secondary)',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={referredColumns.length}
                        style={{
                          textAlign: 'center',
                          padding: '48px 0',
                          color: 'var(--text-faint, #666)',
                          fontSize: 14,
                        }}
                      >
                        No subscribers yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!!referredList.data?.total_count && referredList.data.total_count > 0 && (
              <div style={{marginTop: 12}}>
                <Pagination
                  initialPage={currPage}
                  totalPages={(referredList.data.total_count - 1) / referredList.data.limit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
